// Theme factory. createAppTheme(mode) assembles palette + typography + shadows
// + component overrides into a single MUI theme for the given color mode.
//
// applyCssVarBridge() mirrors the active theme's colors into the legacy :root
// CSS custom properties (--primary, --background, --text, ...) so existing SCSS
// tracks light/dark automatically while pages are migrated incrementally.

import { createTheme } from "@mui/material/styles";

import getPalette from "./palette";
import typography from "./typography";
import getShadows from "./shadows";
import getComponents from "./components";

/**
 * @param {"light"|"dark"} mode
 * @returns {import("@mui/material/styles").Theme}
 */
export const createAppTheme = (mode = "light") => {
  const paletteMode = mode === "dark" ? "dark" : "light";
  return createTheme({
    palette: getPalette(paletteMode),
    typography,
    shadows: getShadows(paletteMode),
    // Surfaces are square by design; only buttons round slightly (see components.js).
    shape: { borderRadius: 0 },
    components: getComponents(),
  });
};

/**
 * Bridge theme colors into the legacy :root CSS variables so SCSS-styled
 * surfaces follow the active theme. Call whenever the mode changes.
 * @param {import("@mui/material/styles").Theme} theme
 */
export const applyCssVarBridge = (theme) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const { palette } = theme;
  const set = (name, value) => root.style.setProperty(name, value);

  set("--primary", palette.primary.main);
  set("--secondary", palette.secondary.main);
  set("--shade", palette.primary.light);
  set("--background", palette.background.default);
  set("--white", palette.background.paper);
  set("--pure-white", palette.background.paper);
  set("--text", palette.text.primary);
  set("--label", palette.text.secondary);
  set("--border", palette.divider);
  set("--passive-button-text", palette.text.disabled);
  set("--error", palette.error.main);
  // Keep the *-dark tokens meaningful in both modes.
  set("--background-dark", palette.mode === "dark" ? palette.background.paper : "#231f20");
  set("--text-dark", palette.mode === "dark" ? palette.text.primary : "#f2f2f2");
  set("--label-dark", palette.mode === "dark" ? palette.text.secondary : "#ccc");

  root.setAttribute("data-theme", palette.mode);
};

export default createAppTheme;
